import ToggleButtonGroup from '@/components/ui/ToggleButtonGroup';
import { useGetOpsServicesQuery } from '@/store/api/services/operations/opsServices/opsServices';
import { useGetUsersAthenaQuery } from '@/store/api/services/users/users';
import type { AssignmentType } from '@/types/serviceTicket';
import { FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

const ASSIGNMENT_TYPE_OPTIONS = [
  { label: 'Internal', value: 'internal' },
  { label: 'External', value: 'external' },
];

interface TechnicianAssignmentFieldProps {
  assignmentType: AssignmentType;
  technicianId: string;
  opsServiceId: string;
  // Switching the type clears both selections, so a submit only ever carries one of them.
  onAssignmentTypeChange: (assignmentType: AssignmentType) => void;
  onTechnicianChange: (technicianId: string) => void;
  onOpsServiceChange: (opsServiceId: string) => void;
  // Label for the empty option. Omit it to make the empty option a disabled placeholder.
  unassignLabel?: string;
  error?: string;
}

/**
 * Internal / External assignee picker shared by the create form, the Edit drawer and the Assign
 * modal. Internal lists Nonce users with the Machine Dispatch/Tech role, External lists the
 * operator's active Ops Services (managed in Configs → Ops Services).
 */
const TechnicianAssignmentField = ({
  assignmentType,
  technicianId,
  opsServiceId,
  onAssignmentTypeChange,
  onTechnicianChange,
  onOpsServiceChange,
  unassignLabel,
  error,
}: TechnicianAssignmentFieldProps) => {
  // Fetch users with Machine Dispatch/Tech role (role_id = 5) from athena
  const { data: usersData } = useGetUsersAthenaQuery({ role_id: 5 });
  const users = useMemo(() => usersData?.data || [], [usersData]);

  const { data: opsServicesData } = useGetOpsServicesQuery();
  // Keep the currently assigned vendor selectable even if it was deactivated since, otherwise the
  // Select would render blank for that ticket.
  const opsServices = useMemo(
    () =>
      (opsServicesData?.data || []).filter(
        (opsService) => opsService.is_active || opsService.id === opsServiceId,
      ),
    [opsServicesData, opsServiceId],
  );

  const isExternal = assignmentType === 'external';

  return (
    <Stack spacing={2}>
      <ToggleButtonGroup
        exclusive
        value={assignmentType}
        options={ASSIGNMENT_TYPE_OPTIONS}
        onChange={(_, value: AssignmentType | null) => {
          // MUI emits null when the selected button is clicked again; keep the current type.
          if (value) onAssignmentTypeChange(value);
        }}
        sx={{ alignSelf: 'flex-start' }}
        data-test="service-ticket-assignment-type-toggle"
      />
      <FormControl fullWidth size="small" error={!!error}>
        <InputLabel>{isExternal ? 'Ops Service' : 'Assigned Technician'}</InputLabel>
        <Select
          label={isExternal ? 'Ops Service' : 'Assigned Technician'}
          value={isExternal ? opsServiceId : technicianId}
          onChange={(e) =>
            isExternal ? onOpsServiceChange(e.target.value) : onTechnicianChange(e.target.value)
          }
          displayEmpty
          data-test={
            isExternal ? 'service-ticket-ops-service-select' : 'service-ticket-technician-select'
          }
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
        >
          <MenuItem value="" disabled={!unassignLabel}>
            {unassignLabel ? (
              <em>{unassignLabel}</em>
            ) : isExternal ? (
              'Select Ops Service'
            ) : (
              'Select Technician'
            )}
          </MenuItem>
          {isExternal
            ? opsServices.map((opsService) => (
                <MenuItem key={opsService.id} value={opsService.id}>
                  {opsService.name}
                </MenuItem>
              ))
            : users.map((user) => (
                <MenuItem key={user.id} value={user.id?.toString() || ''}>
                  {user.first_name && user.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user.email || 'Unknown'}
                </MenuItem>
              ))}
        </Select>
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
            {error}
          </Typography>
        )}
        {isExternal && opsServicesData && opsServices.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
            No active Ops Services. Add vendors in Configs → Ops Services.
          </Typography>
        )}
      </FormControl>
    </Stack>
  );
};

export default TechnicianAssignmentField;
